import { deferred } from './deferred.mjs';

/**
 * Create a batch manager for a given collection of a data type.
 * Will batch all .get calls given inside a scheduled time window into a singel request.
 *
 * @generic T - The type of the data.
 * @generic Q - item query type
 * @generic C - the context of the batcher passed to the fetcher function
 * @param config BatcherConfig<T, Q>
 * @returns Batcher<T, Q>
 */
const create = (config, memory) => {
    const name = config.name ?? `batcher:${Math.random().toString(16).slice(2)})`;
    const scheduler = config.scheduler ?? windowScheduler(10);
    const devtools = globalThis.__BATSHIT_DEVTOOLS__?.for(name);
    let mem = memory ?? {
        seq: 0,
        batch: new Set(),
        currentRequest: deferred(),
        timer: undefined,
        start: null,
        latest: null,
    };
    devtools?.create({ seq: mem.seq });
    const nextBatch = () => {
        mem.batch = new Set();
        mem.currentRequest = deferred();
        mem.timer = undefined;
        mem.start = null;
        mem.latest = null;
    };
    const fetch = (query) => {
        if (!mem.start)
            mem.start = Date.now();
        mem.latest = Date.now();
        mem.batch.add(query);
        clearTimeout(mem.timer);
        const scheduled = scheduler(mem.start, mem.latest, mem.batch.size);
        devtools?.queue({
            seq: mem.seq,
            query,
            batch: [...mem.batch],
            scheduled,
            latest: mem.latest,
            start: mem.start,
        });
        const fetchBatch = () => {
            const currentSeq = mem.seq;
            const req = config.fetcher([...mem.batch]);
            const currentRequest = mem.currentRequest;
            devtools?.fetch({ seq: currentSeq, batch: [...mem.batch] });
            nextBatch();
            req
                .then((data) => {
                devtools?.data({ seq: currentSeq, data });
                currentRequest.resolve(data);
            })
                .catch((error) => {
                devtools?.error({ seq: currentSeq, error });
                currentRequest.reject(error);
            });
            mem.seq++;
            return req;
        };
        if (scheduled === "immediate") {
            const req = mem.currentRequest;
            fetchBatch();
            return req.value.then((items) => config.resolver(items, query));
        }
        else if (scheduled === "never") {
            return mem.currentRequest.value.then((items) => config.resolver(items, query));
        }
        else {
            mem.timer = setTimeout(fetchBatch, scheduled);
            return mem.currentRequest.value.then((items) => config.resolver(items, query));
        }
    };
    return { fetch };
};
/**
 * Resolve by item field of items when response is an array.
 * Create a euquality check to check if the query matches a given key on the item data.
 *
 * @param key keyof T
 * @returns (item:T extends Array<A>, query: Q) => A
 */
const keyResolver = (key) => (items, query) => items.find((item) => item[key] == query) ?? null;
/**
 * Resolve by record index when response is an object.
 * Create a euquality check to check if the query matches a given key on the item data.
 *
 * @returns (item:T extends Record<_, A>, query: Q) => A
 */
const indexedResolver = () => (itemsIndex, query) => itemsIndex[query] ?? null;
/**
 * Give a window in ms where all queued fetched made within the window will be batched into
 * one singler batch fetch call.
 *
 * @param ms number
 * @returns BatcherScheduler
 */
const windowScheduler = (ms) => (start, latest) => {
    const spent = latest - start;
    return ms - spent;
};
/**
 * Give a buffer time in ms. Will give another buffer window when queueing a fetch.
 *
 * @param ms number
 * @returns BatcherScheduler
 */
const bufferScheduler = (ms) => () => {
    return ms;
};
/**
 * Same as windowScheduler, will batch calls made within a window of time OR when the max batch size is reached.
 *
 * @param config: {windowMs: number; maxBatchSize: number;}
 * @returns BatcherScheduler
 */
const windowedFiniteBatchScheduler = ({ windowMs, maxBatchSize }) => (start, latest, batchSize) => {
    if (batchSize >= maxBatchSize)
        return "immediate";
    const spent = latest - start;
    return windowMs - spent;
};
/**
 * Will batch calls when the max batch size is reached.
 *
 * @param config: {maxBatchSize: number;}
 * @returns BatcherScheduler
 */
const maxBatchSizeScheduler = ({ maxBatchSize }) => (_start, _latest, batchSize) => {
    if (batchSize >= maxBatchSize)
        return "immediate";
    return "never";
};

export { bufferScheduler, create, indexedResolver, keyResolver, maxBatchSizeScheduler, windowScheduler, windowedFiniteBatchScheduler };
//# sourceMappingURL=index.mjs.map
